import type { QuerySpec } from '@helios/powersync-client-core';

// ─── Row Types ───

export interface FormDefinitionRow {
  id: string;
  org_id: string;
  clinic_id: string | null;
  name: string;
  description: string | null;
  status: string; // 'draft' | 'published' | 'archived'
  version: number;
  fields_schema: string; // JSON text on client
  default_view: string; // 'render' | 'form'
  created_at: string;
  updated_at: string;
  updated_by: string | null;
}

export interface FormTemplatePageRow {
  id: string;
  form_definition_id: string;
  page_number: number;
  image_url: string;
  image_width: number;
  image_height: number;
  label: string | null;
  created_at: string;
  updated_at: string;
}

export interface FormTemplateMappingRow {
  id: string;
  form_definition_id: string;
  page_id: string;
  field_key: string;
  field_type: string; // 'binding' | 'drawing' | 'text' | 'checkbox' | 'signature'
  x_pct: number;
  y_pct: number;
  w_pct: number;
  h_pct: number;
  config: string; // JSON text on client
  sort_order: number;
  created_at: string;
  updated_at: string;
}

// ─── Field Types ───

export type FormFieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'date'
  | 'select'
  | 'multi-select'
  | 'yes-no'
  | 'binding'
  | 'drawing'
  | 'signature';

export interface FormFieldOption {
  id: string;
  label: string;
  value: string;
}

export interface FormFieldDef {
  key: string;
  label: string;
  type: FormFieldType;
  required?: boolean;
  helpText?: string;
  layout?: 'half' | 'full';
  options?: FormFieldOption[];
  /** Validation rules (same schema as @helios/validation Rule[]) */
  validation?: unknown[];
  config?: BindingConfig | DrawingConfig;
}

export interface BindingConfig {
  source: string; // 'patient' | 'encounter'
  path: string; // e.g. 'full_name', 'birth_date'
}

export interface DrawingConfig {
  placeholder?: string;
}

// ─── Queries ───

/**
 * List all form definitions for the org, ordered by updated_at desc.
 */
export function qFormDefinitions(orgId: string): QuerySpec<[string]> {
  return {
    key: ['forms', 'definitions', orgId] as const,
    sql: `SELECT * FROM form_definitions WHERE org_id = ? ORDER BY updated_at DESC`,
    params: [orgId],
    watch: ['form_definitions'],
  };
}

/**
 * Fetch a single form definition by ID.
 */
export function qFormDefinitionById(id: string): QuerySpec<[string]> {
  return {
    key: ['forms', 'definition', id] as const,
    sql: `SELECT * FROM form_definitions WHERE id = ?`,
    params: [id],
    watch: ['form_definitions'],
  };
}

/**
 * List published form definitions for the org.
 */
export function qPublishedFormDefinitions(orgId: string): QuerySpec<[string]> {
  return {
    key: ['forms', 'definitions', 'published', orgId] as const,
    sql: `SELECT * FROM form_definitions WHERE org_id = ? AND status = 'published' ORDER BY name ASC`,
    params: [orgId],
    watch: ['form_definitions'],
  };
}

/**
 * Fetch template pages for a form definition, ordered by page_number.
 */
export function qFormTemplatePages(formDefinitionId: string): QuerySpec<[string]> {
  return {
    key: ['forms', 'pages', formDefinitionId] as const,
    sql: `SELECT * FROM form_template_pages WHERE form_definition_id = ? ORDER BY page_number ASC`,
    params: [formDefinitionId],
    watch: ['form_template_pages'],
  };
}

/**
 * Fetch template mappings for a form definition.
 */
export function qFormTemplateMappings(formDefinitionId: string): QuerySpec<[string]> {
  return {
    key: ['forms', 'mappings', formDefinitionId] as const,
    sql: `SELECT * FROM form_template_mappings WHERE form_definition_id = ? ORDER BY sort_order ASC`,
    params: [formDefinitionId],
    watch: ['form_template_mappings'],
  };
}

/**
 * Fetch template mappings for a specific page.
 */
export function qFormTemplateMappingsByPage(pageId: string): QuerySpec<[string]> {
  return {
    key: ['forms', 'mappings', 'by-page', pageId] as const,
    sql: `SELECT * FROM form_template_mappings WHERE page_id = ? ORDER BY sort_order ASC`,
    params: [pageId],
    watch: ['form_template_mappings'],
  };
}

// ─── Section-based schema (V1) ───

export interface FormSection {
  id: string;
  title: string;
  description?: string;
  fields: FormFieldDef[];
}

export interface FormFieldsSchemaV1 {
  version: 1;
  sections: FormSection[];
}

// ─── Helpers ───

let _uidCounter = 0;
function uid(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${(++_uidCounter).toString(36)}`;
}

/**
 * Parse fields_schema JSON text. Handles both:
 * - V1 section-based: { version: 1, sections: [...] }
 * - Legacy flat array: FormFieldDef[]
 * Always returns V1 shape.
 */
export function parseFieldsSchemaV1(raw: string | null): FormFieldsSchemaV1 {
  if (!raw) return { version: 1, sections: [] };
  try {
    const parsed = JSON.parse(raw);
    // V1 section-based
    if (
      parsed &&
      typeof parsed === 'object' &&
      parsed.version === 1 &&
      Array.isArray(parsed.sections)
    ) {
      return parsed as FormFieldsSchemaV1;
    }
    // Legacy flat array — wrap into one section
    if (Array.isArray(parsed)) {
      return {
        version: 1,
        sections: parsed.length > 0 ? [{ id: uid('s'), title: 'Section 1', fields: parsed }] : [],
      };
    }
    return { version: 1, sections: [] };
  } catch {
    return { version: 1, sections: [] };
  }
}

/** Serialize V1 schema to JSON text. */
export function stringifyFieldsSchemaV1(schema: FormFieldsSchemaV1): string {
  return JSON.stringify(schema);
}

/** Flatten all fields from sections into a single array (for Steps 3-6). */
export function flattenFields(schema: FormFieldsSchemaV1): FormFieldDef[] {
  return schema.sections.flatMap((s) => s.fields);
}

/**
 * @deprecated Use parseFieldsSchemaV1 + flattenFields instead.
 * Kept for backward compatibility during migration.
 */
export function parseFieldsSchema(raw: string | null): FormFieldDef[] {
  return flattenFields(parseFieldsSchemaV1(raw));
}

/**
 * @deprecated Use stringifyFieldsSchemaV1 instead.
 */
export function stringifyFieldsSchema(fields: FormFieldDef[]): string {
  return JSON.stringify({ version: 1, sections: [{ id: uid('s'), title: 'Section 1', fields }] });
}
